/**
 * Pedidos de Amazon: tu dirección privada para reenviar los emails de Amazon desde Gmail, el código de Google para confirmar
 * el reenvío y lo apuntado. El servidor apunta cada email en _Pedidos al llegar; los que no pudo (Google no respondía) se
 * apuntan solos al abrir esta pantalla, y los que no sabe leer se quedan para guardarlos como .eml. «Subir .eml» lee
 * emails guardados en este dispositivo (pedidos de antes) y se apuntan con «Apuntar en tu libro».
 */
import PostalMime from 'postal-mime';
import { AMAZON_KIND_LABEL, ORDER_KIND_LABEL, orderRows, parseAmazonMail, type AmazonMail, type OrderRow } from '@pf/workbook';
import { api, type MailInbox, type MailItem } from './adapters/api-client.js';
import { $, esc, dayLabel, money, chev, signClass } from './ui/format.js';
import { pendingCharges } from './app/amazon-match.js';
import { toast, stickyBar } from './ui/feedback.js';
import { frame, setView } from './views.js';
import type { EditState, Editor } from './edit-views.js';

interface Read { item: MailItem; subject: string; date: string; from: string; mail: AmazonMail; text: string; raw: ArrayBuffer }

/** Día (aaaa-mm-dd, hora de aquí) de una fecha de email. */
const localDay = (d: string) => { const t = new Date(d); const p = (n: number) => String(n).padStart(2, '0'); return Number.isNaN(t.getTime()) ? d.slice(0, 10) : `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`; };
const shortId = (id: string) => `…${id.slice(-7)}`;

/** Del email de Google para confirmar el reenvío: el código y el enlace de confirmación. */
export function gmailConfirm(text: string): { code: string; link: string } {
  const code = /(?:c[oó]digo de confirmaci[oó]n|confirmation code)\D{0,30}(\d{6,12})/i.exec(text)?.[1] ?? '';
  const link = /https:\/\/mail(?:-settings)?\.google\.com\/mail\/[^\s"<>)]+/.exec(text)?.[0] ?? '';
  return { code, link };
}

async function readOne(item: MailItem, raw: ArrayBuffer): Promise<Read> {
  const m = await new PostalMime().parse(raw);
  const text = m.text || (m.html ?? '').replace(/<[^>]+>/g, ' ');
  const subject = m.subject ?? '', from = m.from && 'address' in m.from ? m.from.address ?? '' : '', date = localDay(m.date ?? item.receivedAt);
  return { item, subject, date, from, mail: parseAmazonMail({ subject, from, date, text }), text, raw };
}

async function readAll(list: MailItem[]): Promise<Read[]> {
  const out: Read[] = [];
  for (const item of list) out.push(await readOne(item, await api.mail.raw(item.id)));
  return out;
}

/** Emails subidos desde este dispositivo (.eml guardados de Gmail): no pasan por el servidor. */
const LOCAL = 'local:';
const isLocal = (r: Read) => r.item.id.startsWith(LOCAL);
const AMAZON_FROM = /@([a-z0-9-]+\.)*amazon\.(es|com|de|fr|it|co\.uk)$/i;
async function readFiles(files: File[]): Promise<{ read: Read[]; skipped: number }> {
  const out: Read[] = [];
  let skipped = 0;
  for (const f of files.slice(0, 200)) {
    if (f.size > 1_000_000) { skipped++; continue; }
    const r = await readOne({ id: `${LOCAL}${crypto.randomUUID()}`, kind: 'amazon', receivedAt: new Date(f.lastModified).toISOString(), size: f.size }, await f.arrayBuffer()).catch(() => null);
    if (r && AMAZON_FROM.test(r.from)) out.push(r); else skipped++;
  }
  return { read: out, skipped };
}

const download = (r: Read, i: number) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([r.raw], { type: 'message/rfc822' }));
  a.download = `amazon-${r.item.receivedAt.slice(0, 10)}-${i + 1}.eml`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

/** Recargar la pantalla que se ve ahora (al apuntar, la app la vuelve a pintar con el libro nuevo). */
let reloadShown: () => Promise<void> = async () => {};
/** Última vez que se apuntaron solos los pendientes (al apuntar, la pantalla se vuelve a pintar: que no lo repita en bucle). */
let autoAt = 0;

export function viewAmazon(s: EditState, ed: Editor) {
  frame(s, { title: 'Pedidos de Amazon', tab: 'mas', back: true });
  stickyBar('');
  if (s.demo) return setView('<section class="card"><div class="sub">En la demostración no se reciben emails.</div></section>');
  let read: Read[] = [];

  const setupCard = (i: MailInbox) => !i.address
    ? `<section class="card"><h2>Tu dirección para Amazon</h2>
        <div class="sub">Para repartir cada cobro de Amazon entre los artículos que compraste, la app necesita tus emails de pedido. Tu Gmail se los reenvía a una dirección privada tuya; al llegar cada uno, la app apunta el pedido en tu libro (solo artículos, cantidades e importes) y borra el email. Nadie más los ve.</div>
        <div class="actions"><button class="btn" id="mkInbox">Crear mi dirección</button></div></section>`
    : `<section class="card"><h2>Tu dirección para Amazon</h2>
        <div class="mail-addr"><code id="addr">${esc(i.address)}</code> <button class="btn small secondary" id="copyAddr">Copiar</button></div>
        <div class="sub hint">Solo acepta emails de Amazon reenviados por tu Gmail (${esc(i.forwarder)}). Lo demás se rechaza.</div>
        <details style="margin-top:10px"><summary class="sub">Cómo configurarlo en Gmail (una vez, desde el ordenador)</summary>
          <ol class="mail-steps">
            <li>En Gmail, <b>⚙ → Ver todos los ajustes → Reenvío y correo POP/IMAP → Añadir una dirección de reenvío</b> y pega tu dirección.</li>
            <li>Google manda un código a esa dirección: aparecerá aquí abajo en un minuto (toca «Comprobar»). Escríbelo en Gmail, o abre el enlace.</li>
            <li><b>No</b> actives «Reenviar una copia del correo entrante». Crea un <b>filtro</b>: en la búsqueda, <code>from:(amazon.es) -from:(store-news@amazon.es)</code> → <b>Crear filtro</b> → marca <b>«Reenviarlo a»</b> tu dirección → <b>Crear filtro</b>.</li>
          </ol></details>
        <div class="actions"><button class="btn small secondary" id="check">Comprobar</button>
          <button class="btn small secondary" id="newInbox">Cambiar de dirección</button> <button class="btn small secondary" id="rmInbox">Quitar</button></div></section>`;

  const confirmCard = () => {
    const c = read.filter(r => r.item.kind === 'gmail-confirm');
    if (!c.length) return '';
    const g = gmailConfirm(c[c.length - 1]!.text);
    return `<section class="card attention"><h2>Confirma el reenvío en Gmail</h2>
      ${g.code ? `<div>Código de confirmación: <b class="mail-code">${esc(g.code)}</b></div>` : '<div class="sub">Ha llegado el email de Google, pero no encuentro el código.</div>'}
      ${g.link ? `<div class="sub">O abre el <a href="${esc(g.link)}" target="_blank" rel="noopener noreferrer">enlace de Google para confirmarlo</a>.</div>` : ''}
      <div class="actions"><button class="btn small" id="confirmDone">Ya está confirmado</button></div></section>`;
  };

  const amazonMails = () => read.filter(r => r.item.kind === 'amazon');
  const itemsHtml = (r: Read) => r.mail.parts.map(p => `<div class="amz-part"><div class="sub">Pedido ${esc(shortId(p.orderId))}${p.total !== null ? ` · ${r.mail.kind === 'refund' ? 'reembolso' : 'total'} <b>${money(p.total)}</b>` : ''}</div>
      ${p.items.map(x => `<div class="amz-item"><span>${esc(x.name)}${x.qty > 1 ? ` <span class="sub">×${x.qty}</span>` : ''}</span>${x.amount !== null ? `<span>${money(x.amount)}</span>` : ''}</div>`).join('')}</div>`).join('');

  const listCard = (i: MailInbox) => {
    const mails = amazonMails();
    const rows = mails.flatMap(r => orderRows(r.mail));
    const known = mails.filter(r => r.mail.kind !== 'other');
    return `<section class="card"><h2>Emails de Amazon recibidos</h2>
      ${mails.length ? `<div class="sub hint">Normalmente no verás nada aquí: cada email se apunta solo al llegar. Aquí quedan los que subes desde este dispositivo y los que la app no sabe leer (se borran solos a los 14 días): guárdalos con «.eml» y pásamelos. «Apuntar en tu libro» los guarda en la pestaña oculta <b>_Pedidos</b>; los avisos sin importe (en reparto, entregado…) solo se borran.</div>
        <div class="list">${mails.map((r, n) => `<div class="row amz-mail"><div class="main"><div class="title">${esc(AMAZON_KIND_LABEL[r.mail.kind])} <span class="sub">· ${esc(dayLabel(r.date))}</span></div>
          ${r.mail.kind === 'other' ? `<div class="meta">${esc(r.subject || '(sin asunto)')}</div>` : itemsHtml(r)}</div>
          <div class="amz-btns"><button class="btn small secondary" data-eml="${n}" title="Guardar el email en este dispositivo">.eml</button> <button class="btn small secondary" data-del="${esc(r.item.id)}">Borrar</button></div></div>`).join('')}</div>`
        : `<div class="empty">${i.pending ? `${i.pending} pendiente${i.pending === 1 ? '' : 's'}: toca «Leer»` : 'Nada todavía'}</div>`}
      <div class="actions">${known.length ? `<button class="btn small" id="saveOrders">${rows.length ? `Apuntar en tu libro (${new Set(rows.map(x => x.ref)).size})` : 'Borrar los avisos'}</button> ` : ''}${i.address ? `<button class="btn small${known.length ? ' secondary' : ''}" id="readMail">Leer</button> ` : ''}<label class="btn small secondary">Subir .eml<input type="file" id="emlFiles" accept=".eml,message/rfc822" multiple hidden></label></div>
      <div class="sub hint">¿Pedidos de antes? En Gmail, abre el email de Amazon → ⋮ → «Descargar mensaje» y súbelo aquí. Se lee en este dispositivo.</div></section>`;
  };

  /** Lo ya apuntado en _Pedidos, lo más reciente primero (un bloque por email). */
  const savedCard = () => {
    const groups = new Map<string, OrderRow[]>();
    for (const o of s.c.wb.orders ?? []) (groups.get(o.ref) ?? groups.set(o.ref, []).get(o.ref)!).push(o);
    const list = [...groups.values()].sort((a, b) => b[0]!.date.localeCompare(a[0]!.date)).slice(0, 15);
    if (!list.length) return '';
    const byId = new Map(s.c.wb.movements.map(m => [m.id, m]));
    return `<section class="card"><h2>Apuntados en tu libro</h2><div class="sub hint">Los últimos ${list.length}. Cuando llega el cobro de tu banco, la app lo encuentra por el importe y te propone repartirlo entre sus artículos.</div>
      <div class="list">${list.map(g => { const o = g[0]!; return `<div class="row amz-mail"><div class="main"><div class="title">${esc(ORDER_KIND_LABEL[o.kind])} <span class="sub">· ${esc(dayLabel(o.date))} · pedido ${esc(shortId(o.orderId))}</span></div>
        ${g.map(x => `<div class="amz-item"><span>${esc(x.item)}${x.qty > 1 ? ` <span class="sub">×${x.qty}</span>` : ''}</span>${x.amount !== null ? `<span>${money(x.amount)}</span>` : ''}</div>`).join('')}
        ${o.total !== null ? `<div class="amz-item"><span class="sub">${o.kind === 'refund' ? 'Reembolso' : 'Total'}</span><b>${money(o.total)}</b></div>` : ''}
        ${(() => { const m = byId.get(g.find(x => x.movementId)?.movementId ?? ''); return m ? `<div class="sub"><a href="#/mov/${encodeURIComponent(m.id)}">✓ ${m.amount > 0 ? 'Devolución' : 'Cobro'} del ${esc(dayLabel(m.date))} · ${esc(m.account)}</a></div>` : ''; })()}</div></div>`; }).join('')}</div></section>`;
  };

  /** Cobros y devoluciones de Amazon del banco con su email encontrado, aún sin repartir. */
  const chargesCard = () => {
    const list = pendingCharges(s.c.wb);
    if (!list.length) return '';
    return `<section class="card attention"><h2>Por repartir</h2><div class="sub hint">Cobros y devoluciones de tu banco con su email de Amazon encontrado.</div>
      <div class="list">${list.map(c => `<a class="row" href="#/amazon-cobro/${encodeURIComponent(c.movement.id)}" style="text-decoration:none;color:inherit"><div class="main"><div class="title">${esc(c.options[0]!.flatMap(g => g.items.map(x => x.item)).join(' · ').slice(0, 80))}</div>
        <div class="meta">${esc(dayLabel(c.movement.date))} · ${esc(c.movement.account)}${c.movement.amount > 0 ? ' · devolución' : ''}${c.sure ? '' : ` · elige el ${c.movement.amount > 0 ? 'reembolso' : 'envío'}`}</div></div><div class="amount ${signClass(c.movement.amount)}">${money(c.movement.amount)}</div>${chev}</a>`).join('')}</div></section>`;
  };

  const draw = (i: MailInbox) => {
    setView(`${chargesCard()}${setupCard(i)}${confirmCard()}${listCard(i)}${savedCard()}`);
    $('#view').onclick = e => onClick(e as MouseEvent, i);
    $('#view').onchange = async e => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'emlFiles' || !input.files?.length) return;
      const got = await readFiles([...input.files]);
      read = [...read, ...got.read];
      if (got.skipped) toast(`${got.skipped} fichero${got.skipped === 1 ? '' : 's'} no ${got.skipped === 1 ? 'es un email' : 'son emails'} de Amazon`, 'error');
      draw(i);
    };
  };
  /** Apunta en el libro lo que se sabe leer y lo borra del servidor (los que no, se quedan). */
  const saveKnown = async (list: Read[]) => {
    const known = list.filter(r => r.item.kind === 'amazon' && r.mail.kind !== 'other');
    if (!known.length) return;
    const rows = known.flatMap(r => orderRows(r.mail));
    if (rows.length) await ed.edit([{ type: 'add-orders', rows }], `Pedidos de Amazon (${new Set(rows.map(x => x.ref)).size})`);
    // Ya están en la cola del libro (que reintenta sola): se pueden borrar del servidor
    const fromServer = known.filter(r => !isLocal(r)).map(r => r.item.id);
    if (fromServer.length) await api.mail.ack(fromServer);
    const done = new Set(known.map(r => r.item.id));
    read = read.filter(r => !done.has(r.item.id));
    toast(rows.length ? '✓ Apuntados en tu libro' : '✓ Avisos borrados');
    await reloadShown();
  };
  const load = async (withRead = false) => {
    try {
      const i = await api.mail.inbox();
      // Pendientes que el servidor no pudo apuntar: se apuntan solos (una vez; si vuelve a fallar, queda el botón)
      const auto = !withRead && i.pending > 0 && Date.now() - autoAt > 60000;
      if (withRead || auto || i.confirm) read = [...read.filter(isLocal), ...await readAll(await api.mail.list())];
      if (auto) {
        autoAt = Date.now();
        // saveKnown vuelve a pintar la pantalla; si no había nada que apuntar (solo emails que no sé leer), se pinta aquí
        if (read.some(r => !isLocal(r) && r.item.kind === 'amazon' && r.mail.kind !== 'other')) return void await saveKnown(read.filter(r => !isLocal(r)));
      }
      draw(i);
    } catch (err) { setView(`<div class="error">${esc((err as Error).message)}</div>`); }
  };
  const onClick = async (e: MouseEvent, i: MailInbox) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!b) return;
    try {
      if (b.id === 'mkInbox' || b.id === 'newInbox') {
        if (b.id === 'newInbox' && !confirm('La dirección actual dejará de funcionar y se borra lo pendiente. Tendrás que cambiarla también en Gmail. ¿Seguir?')) return;
        b.disabled = true;
        await api.mail.createInbox();
        toast('✓ Dirección creada');
        read = [];
        await load();
      } else if (b.id === 'rmInbox') {
        if (!confirm('Se quita tu dirección y se borra lo pendiente. Quita también el filtro y el reenvío en Gmail. ¿Seguir?')) return;
        await api.mail.deleteInbox();
        read = [];
        await load();
      } else if (b.id === 'copyAddr') {
        await navigator.clipboard.writeText(i.address ?? '');
        toast('✓ Copiada');
      } else if (b.id === 'check' || b.id === 'readMail') {
        b.disabled = true;
        b.textContent = 'Leyendo…';
        await load(true);
      } else if (b.id === 'confirmDone') {
        await api.mail.ack(read.filter(r => r.item.kind === 'gmail-confirm').map(r => r.item.id));
        read = read.filter(r => r.item.kind !== 'gmail-confirm');
        await load();
      } else if (b.id === 'saveOrders') {
        b.disabled = true;
        await saveKnown(amazonMails());
      } else if (b.dataset.eml) {
        download(amazonMails()[Number(b.dataset.eml)]!, Number(b.dataset.eml));
      } else if (b.dataset.del) {
        if (!b.dataset.del.startsWith(LOCAL)) await api.mail.ack([b.dataset.del]);
        read = read.filter(r => r.item.id !== b.dataset.del);
        await load();
      }
    } catch (err) {
      toast((err as Error).message, 'error');
      b.disabled = false;
    }
  };
  reloadShown = () => load();
  setView('<div class="loading">Consultando…</div>');
  void load();
}
